import { useEffect, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router';
import { useFieldArray, useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { keepPreviousData, useMutation, useQuery } from '@tanstack/react-query';
import { z } from 'zod';
import { api, invalidateData, keys, useMeta } from '../../api.ts';
import { formatBand, formatDateTime } from '../../../shared/format.ts';
import { qsoInputSchema } from '../../../shared/schemas.ts';
import type { Qso, QsoPage } from '../../../shared/types.ts';
import {
  BandPicker,
  ConfirmButton,
  CountryPicker,
  CountrySelect,
  ErrorBox,
  Help,
  Loading,
  ModePicker,
  Pagination,
  Spinner,
  useCountryMap,
  useToast,
} from '../../components/ui.tsx';
import { useUserContext } from './UserLayout.tsx';

const list = (s: string | null) => (s ? s.split(',').filter(Boolean) : []);

export function UserQsosPage() {
  const { user, canEdit } = useUserContext();
  const [params, setParams] = useSearchParams();
  const countries = useCountryMap();
  const toast = useToast();
  const [selected, setSelected] = useState<Set<number>>(new Set());
  const [editing, setEditing] = useState<Qso[] | null>(null);

  const query = params.toString();
  const q = useQuery({
    queryKey: keys.qsos(user.id, query),
    queryFn: () => api.get<QsoPage>(`/users/${encodeURIComponent(user.id)}/qsos?${query}`),
    placeholderData: keepPreviousData,
  });
  useEffect(() => setSelected(new Set()), [query]);

  const [draft, setDraft] = useState(() => ({
    bands: list(params.get('bands')).map(Number),
    modes: list(params.get('modes')),
    countries: list(params.get('countries')).map(Number),
    call: params.get('call') ?? '',
    onePerCountry: params.get('onePerCountry') === '1',
  }));

  const apply = (e?: React.FormEvent) => {
    e?.preventDefault();
    const p = new URLSearchParams();
    if (draft.bands.length) p.set('bands', draft.bands.join(','));
    if (draft.modes.length) p.set('modes', draft.modes.join(','));
    if (draft.countries.length) p.set('countries', draft.countries.join(','));
    if (draft.call.trim()) p.set('call', draft.call.trim());
    if (draft.onePerCountry) p.set('onePerCountry', '1');
    setParams(p);
  };
  const setPage = (page: number) => {
    const p = new URLSearchParams(params);
    p.set('page', String(page));
    setParams(p);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const base = `/users/${encodeURIComponent(user.id)}/qsos`;
  const remove = useMutation({
    mutationFn: (ids: number[]) => api.post<{ deleted: number }>(`${base}/delete`, { ids }),
    onSuccess: (r) => {
      toast.ok(`${r.deleted} QSO:ta poistettu`);
      setSelected(new Set());
      invalidateData();
    },
    onError: toast.error,
  });
  const removeAll = useMutation({
    mutationFn: () => api.del<{ deleted: number }>(base),
    onSuccess: (r) => {
      toast.ok(`Kaikki QSO:t (${r.deleted}) poistettu`);
      invalidateData();
    },
    onError: toast.error,
  });

  const rows = q.data?.rows ?? [];
  const pages = q.data ? Math.ceil(q.data.total / q.data.pageSize) : 1;
  const allSelected = rows.length > 0 && rows.every((r) => selected.has(r.id));
  const toggle = (id: number) =>
    setSelected((s) => {
      const n = new Set(s);
      if (n.has(id)) n.delete(id);
      else n.add(id);
      return n;
    });

  if (editing) {
    return (
      <QsoEditor
        userId={user.id}
        rows={editing}
        onDone={() => {
          setEditing(null);
          setSelected(new Set());
        }}
      />
    );
  }

  return (
    <div className="stack">
      <form className="card" onSubmit={apply}>
        <div className="card-title">
          <h2>Hae yhteyksiä</h2>
          <Help name="OmaQSO" />
        </div>
        <div className="grid grid-2">
          <div className="stack">
            <div>
              <div className="label" style={{ marginBottom: 6 }}>
                Bandit <span className="faint">(tyhjä = kaikki)</span>
              </div>
              <BandPicker value={draft.bands} onChange={(bands) => setDraft({ ...draft, bands })} />
            </div>
            <div>
              <div className="label" style={{ marginBottom: 6 }}>
                Moodit <span className="faint">(päämoodi sisältää alimoodit)</span>
              </div>
              <ModePicker value={draft.modes} onChange={(modes) => setDraft({ ...draft, modes })} />
            </div>
            <div className="form-grid">
              <div className="field">
                <label htmlFor="call">Kutsu</label>
                <input
                  id="call"
                  className="input mono"
                  placeholder="esim. VK9* "
                  value={draft.call}
                  onChange={(e) => setDraft({ ...draft, call: e.target.value })}
                />
              </div>
              <label className="check" style={{ alignSelf: 'end', paddingBottom: 8 }}>
                <input
                  type="checkbox"
                  checked={draft.onePerCountry}
                  onChange={(e) => setDraft({ ...draft, onePerCountry: e.target.checked })}
                />
                Näytä yksi kutsu per maa (QSL-lista)
              </label>
            </div>
          </div>
          <div>
            <div className="label" style={{ marginBottom: 6 }}>
              Maat
            </div>
            <CountryPicker value={draft.countries} onChange={(countries) => setDraft({ ...draft, countries })} />
          </div>
        </div>
        <div className="form-actions" style={{ marginTop: 16 }}>
          <button type="submit" className="btn btn-primary">
            Hae
          </button>
          <button
            type="button"
            className="btn btn-ghost"
            onClick={() => {
              setDraft({ bands: [], modes: [], countries: [], call: '', onePerCountry: false });
              setParams(new URLSearchParams());
            }}
          >
            Tyhjennä
          </button>
        </div>
      </form>

      <ErrorBox error={q.error} />
      <div className="row">
        <span className="muted">
          {q.data ? `${q.data.total.toLocaleString('fi-FI')} yhteyttä` : ''}
          {q.isFetching && (
            <>
              {' '}
              <Spinner />
            </>
          )}
        </span>
        <span className="spacer" />
        <Pagination page={q.data?.page ?? 1} pages={pages} onPage={setPage} />
      </div>

      {q.isLoading ? (
        <Loading />
      ) : (
        <div className="table-wrap">
          <table className="table">
            <thead>
              <tr>
                {canEdit && (
                  <th style={{ width: 1 }}>
                    <input
                      type="checkbox"
                      aria-label="Valitse kaikki"
                      checked={allSelected}
                      onChange={() => setSelected(allSelected ? new Set() : new Set(rows.map((r) => r.id)))}
                    />
                  </th>
                )}
                <th>Kutsu</th>
                <th>Aika (UTC)</th>
                <th className="num">Band</th>
                <th className="num">Taajuus</th>
                <th>Moodi</th>
                <th>Maa</th>
                <th>Prefiksi</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((r) => {
                const c = countries.get(r.countryId);
                return (
                  <tr key={r.id} className={selected.has(r.id) ? 'selected' : undefined}>
                    {canEdit && (
                      <td>
                        <input type="checkbox" aria-label={`Valitse ${r.callSign}`} checked={selected.has(r.id)} onChange={() => toggle(r.id)} />
                      </td>
                    )}
                    <td className="call">{r.callSign}</td>
                    <td className="mono">{formatDateTime(r.qsoDateTime)}</td>
                    <td className="num">{formatBand(r.band)}</td>
                    <td className="num">{r.frequency ? r.frequency.toFixed(3).replace('.', ',') : ''}</td>
                    <td className="mono">{r.mode}</td>
                    <td>
                      {c?.name} {c?.deleted && <span className="badge">poistunut</span>}
                    </td>
                    <td className="mono muted">{c?.code}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
          {!rows.length && <div className="empty">Ei yhteyksiä hakuehdoilla.</div>}
        </div>
      )}

      {canEdit && (
        <div className="form-actions">
          <button className="btn" disabled={!selected.size} onClick={() => setEditing(rows.filter((r) => selected.has(r.id)))}>
            Muokkaa valittuja ({selected.size})
          </button>
          <ConfirmButton
            disabled={!selected.size}
            title="Poista QSO:t"
            message={`Haluatko varmasti poistaa ${selected.size} valittua QSO:ta?`}
            confirmText="Poista"
            onConfirm={() => remove.mutateAsync([...selected])}
          >
            Poista valitut
          </ConfirmButton>
          <span className="spacer" />
          <ConfirmButton
            title="Poista kaikki QSO:t"
            message={
              <>
                Kaikki käyttäjän <span className="call">{user.id}</span> QSO:t poistetaan pysyvästi. Logi täytyy tämän jälkeen ladata
                uudelleen ADIF-tiedostona.
              </>
            }
            confirmText="Poista kaikki"
            onConfirm={() => removeAll.mutateAsync()}
          >
            Poista kaikki QSO:t
          </ConfirmButton>
        </div>
      )}
    </div>
  );
}

/* ---------- valittujen QSO:iden muokkaus ---------- */

const editSchema = z.object({ rows: z.array(qsoInputSchema.extend({ id: z.number() })) });
type EditValues = z.input<typeof editSchema>;

function QsoEditor({ userId, rows, onDone }: { userId: string; rows: Qso[]; onDone: () => void }) {
  const meta = useMeta();
  const toast = useToast();
  const defaults = useMemo<EditValues>(
    () => ({
      rows: rows.map((r) => ({
        id: r.id,
        callSign: r.callSign,
        date: r.qsoDateTime.slice(0, 10),
        time: r.qsoDateTime.slice(11, 19),
        band: r.band,
        mode: r.mode,
        countryId: r.countryId,
        frequency: r.frequency ?? '',
      })),
    }),
    [rows],
  );
  const { register, control, handleSubmit, formState, setValue, watch } = useForm<EditValues, unknown, z.output<typeof editSchema>>({
    resolver: zodResolver(editSchema),
    defaultValues: defaults,
  });
  const { fields } = useFieldArray({ control, name: 'rows' });
  const save = useMutation({
    mutationFn: (d: z.output<typeof editSchema>) => api.put<{ saved: number }>(`/users/${encodeURIComponent(userId)}/qsos`, d),
    onSuccess: (r) => {
      toast.ok(`${r.saved} QSO:ta päivitetty`);
      invalidateData();
      onDone();
    },
    onError: toast.error,
  });
  const errs = formState.errors.rows;

  return (
    <form className="stack" onSubmit={handleSubmit((d) => save.mutate(d))} noValidate>
      <h2>Muokkaa QSO:ita</h2>
      <div className="table-wrap">
        <table className="table compact">
          <thead>
            <tr>
              <th>Kutsu</th>
              <th>Päivä</th>
              <th>Aika</th>
              <th>Band</th>
              <th>Taajuus (MHz)</th>
              <th>Moodi</th>
              <th>Maa</th>
            </tr>
          </thead>
          <tbody>
            {fields.map((f, i) => (
              <tr key={f.id}>
                <td>
                  <input className="input input-sm mono" style={{ width: 120 }} aria-invalid={!!errs?.[i]?.callSign} {...register(`rows.${i}.callSign`)} />
                </td>
                <td>
                  <input type="date" className="input input-sm" aria-invalid={!!errs?.[i]?.date} {...register(`rows.${i}.date`)} />
                </td>
                <td>
                  <input type="time" step={1} className="input input-sm" aria-invalid={!!errs?.[i]?.time} {...register(`rows.${i}.time`)} />
                </td>
                <td>
                  <select className="input-sm" {...register(`rows.${i}.band`)}>
                    {meta.data?.bands.map((b) => (
                      <option key={b.band} value={b.band}>
                        {b.label}
                      </option>
                    ))}
                  </select>
                </td>
                <td>
                  <input className="input input-sm mono" style={{ width: 110 }} aria-invalid={!!errs?.[i]?.frequency} {...register(`rows.${i}.frequency`)} />
                </td>
                <td>
                  <select className="input-sm" {...register(`rows.${i}.mode`)}>
                    {meta.data?.modes.map((m) => (
                      <option key={m.mode} value={m.mode}>
                        {m.mode}
                      </option>
                    ))}
                  </select>
                </td>
                <td style={{ minWidth: 240 }}>
                  <CountrySelect
                    className="input-sm"
                    value={Number(watch(`rows.${i}.countryId`)) || null}
                    invalid={!!errs?.[i]?.countryId}
                    onChange={(v) => setValue(`rows.${i}.countryId`, v ?? '', { shouldValidate: true })}
                  />
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      {errs && <div className="alert alert-error">Tarkista punaisella merkityt kentät.</div>}
      <div className="form-actions">
        <button className="btn btn-primary" type="submit" disabled={save.isPending}>
          {save.isPending ? <Spinner /> : 'Tallenna muutokset'}
        </button>
        <button type="button" className="btn btn-ghost" onClick={onDone}>
          Peruuta
        </button>
      </div>
    </form>
  );
}
